<template>
  <main class="login-page">
    <section class="auth-card">
      <header class="auth-header">
        <h1>PathTracer Login</h1>
        <p class="subtitle">Sign in to your account</p>

        <!-- Closed Beta Banner -->
        <div
          v-if="closedBeta"
          class="beta-banner"
          role="status"
          aria-live="polite"
        >
          <span class="dot" aria-hidden="true"></span>
          <strong>Closed Beta:</strong>
          <span> We’re currently in closed beta. Some features may be unavailable.</span>
        </div>
      </header>

      <form class="auth-form" @submit.prevent="login" novalidate>
        <div class="field">
          <label for="username">Username</label>
          <input
            id="username"
            v-model.trim="username"
            type="text"
            autocomplete="username"
            maxlength="50"
            required
          />
        </div>

        <div class="field">
          <label for="password">Password</label>
          <div class="password-row">
            <input
              id="password"
              v-model.trim="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              maxlength="50"
              required
            />
          <button
  type="button"
  class="toggle"
  @click="showPassword = !showPassword"
  :aria-pressed="showPassword.toString()"
  tabindex="-1"
  data-track="login_show_password_toggle"
>
              {{ showPassword ? 'Hide' : 'Show' }}
            </button>
          </div>
        </div>

<button
  class="primary-button"
  type="submit"
  :disabled="isLoading"
  data-track="login_submit"
  :data-track-props="JSON.stringify({ closed_beta: !!closedBeta })"
>
  {{ isLoading ? 'Logging in...' : 'Login' }}
</button>

        <p v-if="errorMessage" class="error-message" role="alert" aria-live="polite">
          {{ errorMessage }}
        </p>
      </form>

      <div class="signup-row">
        <span>Don’t have an account?</span>
    <!-- go to signup -->
<button
  class="linklike"
  @click="$router.push('/signup')"
  data-track="login_signup_link_click"
>
  Sign up
</button>
      </div>



     <div class="legal-links" role="navigation" aria-label="legal">
  <router-link to="/terms"  target="_blank" data-track="login_view_terms">Terms of Service</router-link>
  <span aria-hidden="true">•</span>
  <router-link to="/privacy" target="_blank" data-track="login_view_privacy">Privacy Policy</router-link>
  <span aria-hidden="true">•</span>
  <router-link to="/fair-use-policy" target="_blank" data-track="login_view_fair_use">Fair Use</router-link>
</div>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import AuthService from '@/services/AuthService';
import { logEvent } from '@/analytics';
export default {
  name: 'LoginPage',
  data() {
    return {
      // Toggle this on/off or wire it to a config endpoint later
      closedBeta: false,

      username: '',
      password: '',
      errorMessage: '',
      isLoading: false,
      showPassword: false
    };
  },
  created() {
    const prefill = this.$route.query.username;
    if (prefill) {
      this.username = prefill;
      this.$router.replace({ query: {} });
    }
    AuthService.isAuthenticated().then(isAuth => {
      if (isAuth) this.$router.push({ name: 'home' });
    });

     logEvent('login_view', { from: this.$route.query?.from || null });
  },
  methods: {
    isValidUsername(name) {
      return /^[a-zA-Z][a-zA-Z0-9_-]{2,49}$/.test(name);
    },
    async login() {
      if (!this.isValidUsername(this.username)) {
        this.errorMessage = 'Invalid username format.';
        return;
      }
      if (this.password.length < 3) {
        this.errorMessage = 'Password must be at least 3 characters.';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const { data } = await axios.post('etherscan/login.php', {
          username: this.username,
          password: this.password
        });

        if (data.status === 'success') {
          localStorage.setItem('token', data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
          localStorage.setItem('username', this.username);
          this.$router.push('/');
          this.$emit('login-success');
        } else {
          this.errorMessage = data.message || 'Login failed.';
        }
      } catch (err) {
        console.error('Login error:', err);
        this.errorMessage = 'An error occurred. Please try again.';
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Local design tokens (light theme, no App.vue changes needed) */
.login-page {
  --card-bg: #ffffff;
  --card-border: #e5e8ec;
  --text: #1f2a37;
  --muted: #6b7280;
  --input-bg: #ffffff;
  --input-border: #cfd6de;
  --input-border-focus: #2c80b4;
  --error: #d43f3a;
  --accent: #35495e;

  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text);
  min-height: calc(100vh - 4rem - 1rem);
}

.auth-card {
  width: min(460px, 92vw);
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.06);
  padding: 1.75rem;
  margin-top: 0;
}

/* Header */
.auth-header { text-align: center; }
.auth-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}
.subtitle {
  margin: 0.4rem 0 0;
  color: var(--muted);
  font-size: 0.95rem;
}

/* Closed Beta Banner */
.beta-banner {
  margin-top: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f3f0ff;
  color: #4b3fb4;
  border: 1px solid rgba(75, 63, 180, 0.15);
  font-size: 0.92rem;
  text-align: left;
}
.beta-banner .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #6b5ce6;
  box-shadow: 0 0 0 4px rgba(107, 92, 230, 0.15);
}

/* Form */
.auth-form { margin-top: 1.2rem; display: grid; gap: 0.9rem; }
.field { display: grid; gap: 0.4rem; }
label { font-size: 0.9rem; color: var(--muted); text-align: left; }

input {
  width: 100%;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text);
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
input:focus {
  border-color: var(--input-border-focus);
  box-shadow: 0 0 0 3px rgba(44, 128, 180, 0.18);
}

/* Password row */
.password-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
}

.toggle {
  border: 1px solid var(--input-border);
  background: #f7fafc;
  color: #4b5563;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.85rem;
}
.toggle:hover { background: #eef2f7; }

/* Buttons */
.primary-button {
  background-color: var(--accent);
  color: #fff;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  width: 100%;
  transition: filter 120ms ease;
}
.primary-button:hover { filter: brightness(1.05); }
.primary-button:disabled { opacity: 0.6; cursor: not-allowed; }

/* Error */
.error-message {
  color: var(--error);
  background: rgba(212, 63, 58, 0.08);
  border: 1px solid rgba(212, 63, 58, 0.25);
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  margin-top: 0.25rem;
  text-align: left;
}

/* Signup + Legal */
.signup-row {
  margin-top: 0.9rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
  color: var(--muted);
  font-size: 0.95rem;
}

.linklike {
  background: transparent;
  border: none;
  color: #0b63a9;
  text-decoration: underline;
  cursor: pointer;
  padding: 0.25rem 0.3rem;
  border-radius: 6px;
}
.linklike:hover { text-decoration: none; }

/* Beta footnote */
.beta-footnote {
  margin-top: 0.6rem;
  color: #6b5ce6;
  font-size: 0.88rem;
  text-align: center;
}

/* Legal */
.legal-links {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  color: var(--muted);
  font-size: 0.9rem;
}
.legal-links a {
  color: var(--muted);
  text-decoration: none;
}
.legal-links a:hover { text-decoration: underline; }

@media (max-width: 480px) { .login-page { padding-top: 5rem; } }
@media (max-height: 700px) { .login-page { padding-top: 4.5rem; } }
</style>
